import { notify, useModal } from '@billy/design-system'
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useInvoicingApiActionSegmentEvents } from '@features/invoices/hooks/useInvoicingApiActionSegmentEvents'
import { DEFAULT_PRODUCT } from '@features/products/constants/defaultProduct'
import { ProductAction } from '@features/products/enums/productAction'
import { ProductPricingModel } from '@features/products/enums/productPricingModel'
import { useUpdateProduct } from '@features/products/hooks/useUpdateProduct'
import { Product } from '@features/products/types/product'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { useForm } from '../../../hooks'
import { PRODUCT_NAME_MAX_LENGTH } from '../constants/productNameMaxLength'
import { getProductEditorFormSchema } from '../elements/ProductEditor/utils/getProductEditorFormSchema'
import { useCreateProduct } from '../hooks/useCreateProduct'
import { useTrackProductAccount } from '../hooks/useTrackProductAccount'
import { ProductEditorFormData } from '../types/productEditorFormData'

interface SubmitFormOptions {
  action: ProductAction
  shouldCreateMore?: boolean
  onSuccess?: (product: Product) => void
}

interface ContextState {
  isSubmitting: boolean
  submitForm: (options: SubmitFormOptions) => void
}

const ProductEditorFormContext = createContext<ContextState | undefined>(undefined)

interface ProductEditorFormContextProviderProps {
  children?: ReactNode
  product: Product
}

export const ProductEditorFormContextProvider = ({
  children,
  product: { id, vatRateId, vatPct, name, ...rest },
}: ProductEditorFormContextProviderProps) => {
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { sendEvent } = useInvoicingApiActionSegmentEvents()
  const { trackProductAccountChange } = useTrackProductAccount()

  const isVatExempted = !!organization?.isVatExempted

  const { Form, handleSubmit } = useForm({
    defaultValues: {
      ...DEFAULT_PRODUCT,
      ...rest,
      productName: name.slice(0, PRODUCT_NAME_MAX_LENGTH),
      vatRate: vatRateId ? { id: vatRateId, rate: vatPct } : undefined,
    },
    validationSchema: getProductEditorFormSchema(t, isVatExempted),
  })

  const { create, isProcessing: isProcessingCreate } = useCreateProduct()
  const { update, isProcessing: isProcessingUpdate } = useUpdateProduct()
  const isProcessing = isProcessingCreate || isProcessingUpdate
  const { close } = useModal(ModalId.ProductEditorModal)

  const handleProductEditorFormCreateSubmit = useCallback(
    (formData: ProductEditorFormData, { shouldCreateMore = false, onSuccess }: SubmitFormOptions) => {
      create(
        {
          ...formData,
          name: formData.productName,
          pricingModel: ProductPricingModel.Price,
          description: formData.description || undefined,
          accountId: formData.accountId || null,
          ...(isVatExempted
            ? { vatPct: 0 }
            : {
                vatPct: formData?.vatRate?.rate || 0,
                vatRateId: formData?.vatRate?.id,
              }),
        },
        {
          onSuccess: ({ data: product }) => {
            queryClient.invalidateQueries([QueryKeys.Products])
            sendEvent.product.created()
            trackProductAccountChange({ previousAccountId: undefined, nextAccountId: formData.accountId })
            if (!shouldCreateMore) {
              close()
            }
            onSuccess?.(product)
            notify({
              id: NotificationKeys.ProductCreated,
              message: t('products.creation_modal.creation_success', { name: formData.productName }),
              variant: 'success',
            })
          },
          onError: () => {
            notify({
              id: NotificationKeys.ProductCreated,
              message: t('products.creation_modal.creation_error'),
              variant: 'error',
            })
          },
        },
      )
    },
    [close, create, isVatExempted, queryClient, sendEvent.product, t, trackProductAccountChange],
  )

  const handleProductEditorFormUpdateSubmit = useCallback(
    (formData: ProductEditorFormData, { shouldCreateMore = false, onSuccess }: SubmitFormOptions) => {
      update(
        {
          ...formData,
          id,
          name: formData.productName,
          pricingModel: ProductPricingModel.Price,
          description: formData.description || undefined,
          accountId: formData.accountId || null,
          ...(isVatExempted
            ? { vatPct: 0 }
            : {
                vatPct: formData?.vatRate?.rate || 0,
                vatRateId: formData?.vatRate?.id,
              }),
        },
        {
          onSuccess: ({ data: product }) => {
            queryClient.invalidateQueries([QueryKeys.Products])
            trackProductAccountChange({ previousAccountId: rest.accountId, nextAccountId: formData.accountId })
            if (!shouldCreateMore) {
              close()
            }
            onSuccess?.(product)
            notify({
              id: NotificationKeys.ProductUpdated,
              message: t('products.creation_modal.update_success', { name: formData.productName }),
              variant: 'success',
            })
          },
          onError: () => {
            notify({
              id: NotificationKeys.ProductUpdated,
              message: t('products.creation_modal.update_error'),
              variant: 'error',
            })
          },
        },
      )
    },
    [close, id, isVatExempted, queryClient, rest.accountId, t, trackProductAccountChange, update],
  )

  const submitForm = useCallback(
    (options: SubmitFormOptions) => {
      handleSubmit((formData) => {
        if ([ProductAction.Create, ProductAction.Duplicate].includes(options.action)) {
          return handleProductEditorFormCreateSubmit(formData, options)
        } else {
          return handleProductEditorFormUpdateSubmit(formData, options)
        }
      })()
    },
    [handleProductEditorFormCreateSubmit, handleProductEditorFormUpdateSubmit, handleSubmit],
  )

  const providerValue = useMemo(
    () => ({
      isSubmitting: isProcessing,
      submitForm,
    }),
    [isProcessing, submitForm],
  )

  return (
    <ProductEditorFormContext.Provider value={providerValue}>
      <Form>{children}</Form>
    </ProductEditorFormContext.Provider>
  )
}

export const useProductEditorForm = () => {
  const context = useContext(ProductEditorFormContext)

  if (!context) {
    throw new Error('ProductEditorFormContextProvider is missing in the module!')
  }

  return context
}
